/**
 * Utility functions for filtering and parsing question data
 */

/**
 * Parse the set name from a uniqueId
 * Format: YEAR_MONTH_SETTYPE_QUESTIONNO
 * Example: "2082_Baishakh_B_Q1" -> "2082_Baishakh"
 */
export const parseSetName = (uniqueId) => {
    if (!uniqueId) return null;
    const parts = uniqueId.split('_');
    if (parts.length < 3) return null;
    return `${parts[0]}_${parts[1]}`;
};

/**
 * Parse the set type from a uniqueId
 * Format: YEAR_MONTH_SETTYPE_QUESTIONNO
 * Example: "2082_Baishakh_B_Q1" -> "B"
 */
export const parseSetType = (uniqueId) => {
    if (!uniqueId) return null;
    const parts = uniqueId.split('_');
    if (parts.length < 3) return null;
    return parts[2]; // Returns "R", "B", or "RB"
};

/**
 * Parse the full set identifier (including type)
 * Format: YEAR_MONTH_SETTYPE_QUESTIONNO
 * Example: "2082_Baishakh_B_Q1" -> "2082_Baishakh_B"
 */
export const parseFullSetName = (uniqueId) => {
    if (!uniqueId) return null;
    const parts = uniqueId.split('_');
    if (parts.length < 3) return null;
    return `${parts[0]}_${parts[1]}_${parts[2]}`;
};

/**
 * Get unique set names from an array of questions
 * Returns array of full set names like ["2082_Baishakh_R", "2081_Bhadra_B"]
 */
export const getAvailableSets = (questions) => {
    if (!Array.isArray(questions)) return [];

    const sets = new Set();
    questions.forEach(q => {
        const fullSet = parseFullSetName(q.uniqueId);
        if (fullSet) sets.add(fullSet);
    });

    return Array.from(sets).sort().reverse(); // Most recent first
};

/**
 * Get available set types from questions
 * Returns object like { R: true, B: true, RB: false }
 */
export const getAvailableSetTypes = (questions) => {
    if (!Array.isArray(questions)) return { R: false, B: false, RB: false };

    const types = { R: false, B: false, RB: false };
    questions.forEach(q => {
        const type = parseSetType(q.uniqueId);
        if (type && types.hasOwnProperty(type)) {
            types[type] = true;
        }
    });

    return types;
};

/**
 * Group sets by year for display
 * Returns object like { "2082": ["2082_Baishakh_R", "2082_Baishakh_B"], ... }
 */
export const groupSetsByYear = (sets) => {
    if (!Array.isArray(sets)) return {};

    const grouped = {};
    sets.forEach(set => {
        const year = set.split('_')[0];
        if (!grouped[year]) {
            grouped[year] = [];
        }
        grouped[year].push(set);
    });

    // Sort years in descending order
    const sortedGrouped = {};
    Object.keys(grouped)
        .sort((a, b) => b - a)
        .forEach(year => {
            sortedGrouped[year] = grouped[year].sort().reverse();
        });

    return sortedGrouped;
};

/**
 * Filter sets based on selected set types
 * If no types selected, returns all sets
 */
export const filterSetsByType = (sets, selectedTypes) => {
    if (!Array.isArray(sets)) return [];
    if (!selectedTypes || (!selectedTypes.R && !selectedTypes.B && !selectedTypes.RB)) {
        return sets; // No filter applied, return all
    }

    return sets.filter(set => {
        const type = set.split('_')[2];
        return selectedTypes[type] === true;
    });
};

/**
 * Check if questions match the selected filters
 */
export const matchesFilters = (question, filters) => {
    if (!question) return false;

    // Marks range filter - only apply if not at default values
    if (filters.marksRange) {
        const [min, max] = filters.marksRange;
        // Skip filtering if at default range (0-20) - this means no active filter
        const isDefaultRange = min === 0 && max === 20;
        if (!isDefaultRange) {
            const marks = parseFloat(question.marks) || 0;
            if (marks < min || marks > max) return false;
        }
    }

    // Set type filter
    if (filters.setTypes && (filters.setTypes.R || filters.setTypes.B || filters.setTypes.RB)) {
        const questionSetType = parseSetType(question.uniqueId);
        if (!filters.setTypes[questionSetType]) return false;
    }

    // Selected sets filter
    if (filters.selectedSets && filters.selectedSets.length > 0) {
        const questionFullSet = parseFullSetName(question.uniqueId);
        if (!filters.selectedSets.includes(questionFullSet)) return false;
    }

    // Tags filter (from SubType)
    if (filters.tags && filters.tags.length > 0) {
        const questionTags = question.tags || [];
        const hasMatchingTag = filters.tags.some(tag => questionTags.includes(tag));
        if (!hasMatchingTag) return false;
    }

    // Chapters filter (from Syllabus)
    if (filters.chapters && filters.chapters.length > 0) {
        const questionSubtopics = Array.isArray(question.chSubtopic) ? question.chSubtopic : [];

        // Check if any subtopic matches any selected chapter or exact subtopic
        const hasMatchingChapter = filters.chapters.some(filter => {
            // Check if filter is an exact subtopic ID (e.g., "1.1", "3.4")
            if (filter.match(/^\d+\.\d+$/)) {
                // Exact subtopic match
                return questionSubtopics.includes(filter);
            }

            // Otherwise, treat as chapter-level filter (e.g., "Chapter 1")
            const chapterNum = filter.replace('Chapter ', '');
            // Check if any subtopic starts with this chapter number
            return questionSubtopics.some(subtopic => subtopic.startsWith(chapterNum + '.'));
        });

        if (!hasMatchingChapter) return false;
    }

    return true;
};
